<template>
  <span>
    <Menu/>

    <AppLayout :showDefaultControls="false" title="Profile">
      <template #content>
        <Loading
          v-if="loading"
          :isLoading="loading"/>
        
        <div v-else class="profile-container">
          
          <!-- User Header -->
          <div class="profile-header mb-4">
            <b-avatar 
              id="user-avatar"
              :src="$store.getters.getUser.picture" 
              size="5rem" 
              class="profile-avatar"></b-avatar>
            
            <div class="profile-info">
              <h2 class="profile-name">{{$store.getters.getUser.firstName}} {{$store.getters.getUser.lastName}}</h2>
              <p class="profile-meta">Member since {{memberSince}}</p>
              <b-link 
                :href="gravatarLink"
                target="_gravatar"
                class="profile-link">
                <small>Change avatar</small>
              </b-link>
            </div>

            <div v-if="isPremiumUser()" class="subscriber-status">
              <div class="status-accent"></div>
              <b-icon-star-fill class="status-icon"></b-icon-star-fill>
              <div class="status-text">
                <div class="status-level">Premium</div>
                <div class="status-tier">Member</div>
              </div>
            </div>
          </div>

          <!-- 20260419 -- Copilot (Claude Sonnet 4.6) -- Replaced anchor-link nav with tab interface to accommodate AI Connections section -->
          <div class="profile-tabs mb-4" role="tablist">
            <button type="button" class="profile-tab" :class="{ active: activeTab === 'account' }"
              role="tab" @click="setTab('account')">Account</button>
            <button type="button" class="profile-tab" :class="{ active: activeTab === 'organization' }"
              role="tab" @click="setTab('organization')">Organization</button>
            <button type="button" class="profile-tab" :class="{ active: activeTab === 'integrations' }"
              role="tab" @click="setTab('integrations')">Integrations</button>
            <button type="button" class="profile-tab" :class="{ active: activeTab === 'data' }"
              role="tab" @click="setTab('data')">Data &amp; Privacy</button>
          </div>

          <!-- Tab: Account -->
          <div v-show="activeTab === 'account'">

          <!-- Personal Details -->
          <b-card class="detail-section-card mb-3" id="personal-details">
            <h4 class="detail-section-title">Personal Details</h4>

            <ProfileForm
              buttonLabel="Save"
              :userData="$store.getters.getUser"
              @submitted:ProfileForm="onSubmitOfProfileForm"/>
          </b-card>

          <!-- Manage Subscription -->
          <b-card class="detail-section-card mb-3" id="subscription" ref="subscription-card">
            <h4 class="detail-section-title">Manage Subscription</h4>

            <SubscriptionManager
              :userData="$store.getters.getUser"/>
          </b-card>

          </div><!-- /account -->

          <!-- Tab: Organization -->
          <div v-show="activeTab === 'organization'">

          <!-- Quick Add Defaults -->
          <b-card class="detail-section-card mb-3" id="quick-add">
            <h4 class="detail-section-title">Quick Add Defaults</h4>
            
            <p class="section-description mb-3">The values set here are applied whenever you use the <i>Quick Add</i> feature to create a new task.</p>
            
            <b-form @submit="onSubmitOfTaskDefaults">
              <b-form-row>
                <b-col>
                  <label class="detail-label" for="priority">PRIORITY</label>
                  <b-form-select 
                    id="priority"
                    v-model="defaultTaskOptions.priority" 
                    validated="true"
                    :options="validPriorities"
                    value-field="item"
                    text-field="name"
                    size="sm"
                  />
                </b-col>

                <b-col>
                  <label class="detail-label" for="status">STATUS</label>
                  <b-form-select 
                    id="status"
                    v-model="defaultTaskOptions.status" 
                    validated="true"
                    :options="validStatuses"
                    value-field="item"
                    text-field="name"
                    size="sm"
                  />
                </b-col>
              </b-form-row>

              <b-form-row class="mt-3">
                <b-col>
                  <label class="detail-label" for="folder">FOLDER</label>
                  <b-form-select 
                    id="folder"
                    v-model="defaultTaskOptions.folder" 
                    validated="true"
                    :options="$store.getters.getFolders"
                    size="sm"
                  />
                </b-col>
              
                <b-col>
                  <label class="detail-label" for="context">CONTEXT</label>
                  <b-form-select 
                    id="context"
                    v-model="defaultTaskOptions.context" 
                    validated="true"
                    :options="$store.getters.getContexts"
                    value-field="name"
                    text-field="name"
                    size="sm"
                  />
                </b-col>
              </b-form-row>

              <b-form-row class="mt-3">
                <b-col>
                  <label class="detail-label" for="start">START</label>
                  <b-form-select 
                    id="start"
                    v-model="defaultTaskOptions.start" 
                    validated="true"
                    :options="defaultDateOptions"
                    value-field="item"
                    text-field="name"
                    size="sm"
                  />
                </b-col>

                <b-col>
                  <label class="detail-label" for="due">DUE</label>
                  <b-form-select 
                    id="due"
                    v-model="defaultTaskOptions.due" 
                    validated="true"
                    :options="defaultDateOptions"
                    value-field="item"
                    text-field="name"
                    size="sm"
                  />
                </b-col>
              </b-form-row>

              <b-row class="mt-4">
                <b-col cols="12" style="text-align:right;">
                  <b-button
                    class="mr-2"
                    variant="success"
                    type="submit"
                  >
                    Save
                  </b-button>
                </b-col>
              </b-row>
            </b-form>
          </b-card>

          <!-- Contexts -->
          <b-card class="detail-section-card mb-3" id="contexts">
            <h4 class="detail-section-title">Contexts</h4>
            
            <p class="section-description mb-3">
              Use the toolbar to create a new context or to change how your contexts are sorted through the application.
              The changes made here do not take effect until the <i>Save</i> button is clicked.  
            </p>
          
            <!-- 20260416 -- Copilot (Claude Sonnet 4.6) -- Modernized context action bar: sort left, primary Add button right, toggles replace separate up/down buttons -->
            <div class="section-action-bar mb-3">
              <div class="action-bar-sort">
                <span class="action-bar-label">Sort</span>
                <b-button
                  size="sm"
                  variant="outline-secondary"
                  class="action-bar-sort-btn"
                  @click="toggleSortContextsAlpha"
                >
                  <b-icon :icon="contextAlphaSortDirection > 0 ? 'sort-alpha-down' : 'sort-alpha-up'" />
                  Name
                </b-button>
                <b-button
                  size="sm"
                  variant="outline-secondary"
                  class="action-bar-sort-btn"
                  @click="toggleSortContextsCustom"
                >
                  <b-icon :icon="contextNumericSortDirection > 0 ? 'sort-numeric-down' : 'sort-numeric-up'" />
                  Order
                </b-button>
              </div>
              <b-button
                size="sm"
                variant="primary"
                class="action-bar-add-btn"
                @click="addContext()"
              >
                <b-icon-plus />
                Add Context
              </b-button>
            </div>

            <!-- 20260416 -- Copilot (Claude Sonnet 4.6) -- Fixed column widths: 1+4+1+2+2+2=12; detail-label matches other section label typography -->
            <b-row class="mb-2">
              <b-col cols="1" />
              <b-col cols="4"><span class="detail-label">Name</span></b-col>
              <b-col cols="1" class="text-center"><span class="detail-label">Color</span></b-col>
              <b-col cols="2" class="text-center"><span class="detail-label">Private</span></b-col>
              <b-col cols="2" class="text-center"><span class="detail-label">Active</span></b-col>
              <b-col cols="2"><span class="detail-label">Order</span></b-col>
            </b-row>
          
            <b-row 
              v-for="(context, index) in localContexts" 
              :key="index"
              :class="{'bg-danger': context.deleteOp, 'p-2': true, 'bg-success': context.addOp}"
              class="mb-2"
            >
              <b-col cols="1">
                <b-button 
                  v-if="typeof context.deleteOp == 'undefined' || context.deleteOp === null || !context.deleteOp"
                  @click="toggleContextField(index, 'deleteOp')" 
                  :id="'button_delete_context_' + index"
                  size="sm"
                  variant="white">
                  
                  <b-icon-trash variant="danger"/>
                
                </b-button>

                <b-tooltip 
                  v-if="typeof context.deleteOp == 'undefined' || context.deleteOp === null || !context.deleteOp"
                  :target="'button_delete_context_' + index"
                  triggers="hover"
                  variant="danger">
                
                  Click to delete this context.

                </b-tooltip>

                <b-button 
                  v-if="context.deleteOp"
                  @click="toggleContextField(index, 'deleteOp')" 
                  :id="'button_undo_context_' + index"
                  size="sm"
                  variant="white">
                  
                  <b-icon-arrow-counterclockwise variant="warning"/>
                
                </b-button>

                <b-tooltip 
                  v-if="context.deleteOp"
                  :target="'button_undo_context_' + index"
                  triggers="hover"
                  variant="warning">
                
                  Click to undo deletion.

                </b-tooltip>
              </b-col>
              
              <b-col cols="4">
                <b-form-input
                  v-model.trim="context.name"
                  size="sm"
                />
                <b-form-valid-feedback :state="contextValid[index].name">
                  Looks good!
                </b-form-valid-feedback>
                <b-form-invalid-feedback :state="contextValid[index].name">
                  A name for the context must be provided.
                </b-form-invalid-feedback>
              </b-col>

              <!-- 20260418 -- Copilot (Claude Sonnet 4.6) -- Color swatch picker; free for all users -->
              <b-col cols="1" class="d-flex justify-content-center align-items-center">
                <color-swatch-picker
                  :value="context.color || null"
                  @input="context.color = $event"
                />
              </b-col>

              <b-col cols="2" class="d-flex justify-content-center">
                <b-form-checkbox-compat
                  :model-value="context.private"
                  @update:modelValue="context.private = $event"
                  name="private"
                  value="Y"
                  unchecked-value="N"
                />
              </b-col>

              <b-col cols="2" class="d-flex justify-content-center">
                <b-form-checkbox-compat
                  :model-value="context.active"
                  @update:modelValue="context.active = $event"
                  name="active"
                  value="Y"
                  unchecked-value="N"
                />
              </b-col>

              <b-col cols="2">
                <b-form-input
                  v-model.trim="context.customOrder"
                  type="number"
                  size="sm"
                />
              </b-col>
            </b-row>

            <b-row class="mt-4">
              <b-col cols="12" style="text-align:right;">
                <b-button
                  ref="contextFormResetButton"
                  class="mr-2"
                  variant="warning"
                  @click="onCancelOfContextForm()"
                >
                  Cancel
                </b-button>
                <b-button
                  ref="contextFormSubmitButton"
                  class="mr-2"
                  variant="success"
                  @click="onSubmitOfContextForm()"
                >
                  Save
                </b-button>
              </b-col>
            </b-row>
          </b-card>
        
          <!-- Folders -->
          <b-card class="detail-section-card mb-3" id="folders">
            <h4 class="detail-section-title">Folders</h4>

            <p class="section-description mb-3">
              Use the toolbar to create a new folder or to change how your folders are sorted through the application.
              The changes made here do not take effect until the <i>Save</i> button is clicked.
            </p>

            <FolderManagement
              @cancelled:FolderManagement="makeToast"
              @submitted:FolderManagement="makeToast"/>
          </b-card>

          </div><!-- /organization -->

          <!-- Tab: Integrations -->
          <div v-show="activeTab === 'integrations'">

          <!-- Reminders -->
          <b-card class="detail-section-card mb-3" id="reminders">
            <h4 class="detail-section-title">Reminders</h4>

            <ReminderConfiguration
              :userData="$store.getters.getUser"
              @submitted:ReminderConfigurationForm="onSubmitOfReminderConfigurationForm"/>
          </b-card>

          <!-- AI Connections -->
          <!-- 20260419 -- Copilot (Claude Sonnet 4.6) -- AI Connections embedded as Profile section; moved from standalone /settings/mcp route -->
          <b-card class="detail-section-card mb-3" id="ai-connections">
            <h4 class="detail-section-title">AI Connections</h4>
            <p class="section-description mb-3">
              AI assistants can connect directly to Principal Task using the
              <strong>Model Context Protocol (MCP)</strong>. Once connected, they can read your tasks,
              create new ones, complete them, and more — without you copying and pasting.
              Each connection is scoped to specific permissions and can be revoked at any time.
            </p>
            <div class="mcp-server-url mb-1">
              <span class="section-description mr-2"><strong>To connect,</strong> add this MCP server URL in your assistant's settings:</span>
              <code class="mcp-url-code">{{ mcpServerUrl }}</code>
              <!-- 20260419 -- Copilot (Claude Sonnet 4.6) -- Copy-to-clipboard button for MCP server URL -->
              <button class="mcp-url-copy" :class="{ 'mcp-url-copy--copied': mcpUrlCopied }" @click="copyMcpUrl" :title="mcpUrlCopied ? 'Copied!' : 'Copy URL'">
                <svg v-if="!mcpUrlCopied" xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <rect x="9" y="2" width="6" height="4" rx="1"/>
                  <path d="M7 4H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-2"/>
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </button>
            </div>
            <p class="mcp-footnote mb-4">
              Currently supported by
              <a href="https://support.claude.com/en/articles/10949351-getting-started-with-local-mcp-servers-on-claude-desktop" target="_blank" rel="noopener">Claude</a>
              (Anthropic). OpenAI and Google offer limited support for custom MCP server connections.
              · <a href="#" class="mcp-limits-trigger" @click.prevent="showMcpLimitsModal = true">View usage limits</a>
            </p>

            <McpSettings />
          </b-card>

          </div><!-- /integrations -->

          <!-- Tab: Data & Privacy -->
          <div v-show="activeTab === 'data'">

          <!-- Shared Links -->
          <!-- 20260507 -- Copilot (Claude Sonnet 4.6) -- b-row/b-col layout to match rest of profile page -->
          <b-card class="detail-section-card mb-3" id="shared-links">
            <h4 class="detail-section-title">Shared Links</h4>
            <p class="section-description mb-3">
              Shared links let you send a read-only snapshot of a task to anyone — no account required.
              Links expire automatically, or you can revoke them here at any time.
            </p>

            <div v-if="!sharesLoaded && !sharesLoading">
              <b-button size="sm" variant="outline-secondary" @click="loadSharedLinks">
                Load shared links
              </b-button>
            </div>

            <div v-if="sharesLoading">
              <b-spinner small class="mr-1" />
              <span class="section-description">Loading…</span>
            </div>

            <p v-if="sharesLoaded && allShares.length === 0" class="section-description mb-0">
              You have no shared links.
            </p>

            <!-- 20260510 -- Copilot (Claude Sonnet 4.6) -- Responsive shared links table: header hidden on mobile,
                 data columns stack on xs and go side-by-side on md+ -->
            <template v-if="sharesLoaded && allShares.length > 0">
              <!-- Column headers: hidden on mobile, visible md+ -->
              <b-row class="mb-2 d-none d-md-flex">
                <b-col md="3"><span class="detail-label">Task</span></b-col>
                <b-col md="4"><span class="detail-label">Link</span></b-col>
                <b-col md="2" class="text-center"><span class="detail-label">Status</span></b-col>
                <b-col md="2"><span class="detail-label">Expires</span></b-col>
                <b-col md="1" />
              </b-row>
              <!-- Data rows: stacked on mobile, tabular on md+ -->
              <b-row
                v-for="s in allShares"
                :key="s.token"
                class="mb-3 mb-md-2 align-items-md-center border-bottom border-md-0 pb-2 pb-md-0"
              >
                <b-col cols="12" md="3" class="text-truncate">
                  <span class="detail-label d-inline d-md-none mr-1">Task:</span>
                  <a :href="`/tasks/detail/${s.taskId}`" target="_blank" rel="noopener">{{ s.taskTitle || '—' }}</a>
                </b-col>
                <b-col cols="12" md="4" class="text-truncate">
                  <span class="detail-label d-inline d-md-none mr-1">Link:</span>
                  <a :href="`${origin}/share/${s.token}`" target="_blank" rel="noopener" class="text-monospace small">{{ s.token }}</a>
                </b-col>
                <b-col cols="6" md="2" class="text-md-center">
                  <span class="detail-label d-inline d-md-none mr-1">Status:</span>
                  <span class="badge" :class="s.status === 'active' ? 'badge-success' : 'badge-secondary'">
                    {{ s.status }}
                  </span>
                </b-col>
                <b-col cols="6" md="2" class="small text-muted">
                  <span class="detail-label d-inline d-md-none mr-1">Expires:</span>
                  {{ formatSharedLinkDate(s.expiresAt) }}
                </b-col>
                <b-col cols="12" md="1" class="text-md-right">
                  <b-button
                    v-if="s.status === 'active'"
                    size="sm"
                    variant="outline-danger"
                    @click="revokeSharedLink(s.token)"
                  >Revoke</b-button>
                </b-col>
              </b-row>
            </template>
          </b-card>

          <!-- 20260510 -- Copilot (Claude Sonnet 4.6) -- Cypress tests for Shared Links added -->
          <!-- API tests: cypress/support/api-helpers/v3/shares.js -->
          <!-- UI tests: cypress/support/page-objects/profile.js (validateSharedLinksSection) -->

          <!-- Data Import -->
          <b-card class="detail-section-card mb-3" id="data-import">
            <h4 class="detail-section-title">Data Import</h4>
            
            <b-form
              ref="importForm"
              @submit="onSubmitOfImportForm"
              @reset="onCancelOfImportForm"
            >
              <b-form-row>
                <b-col>
                  <label class="detail-label" for="source">SOURCE</label>
                  <b-form-select 
                    id="source"
                    v-model="importOptions.source" 
                    validated="true"
                    :state="importFormValidation.source"
                    :options="validImportSources"
                    value-field="item"
                    text-field="name"
                    size="sm"
                  />
                  <b-form-text id="source-help-block">
                    <small>The name of the software/website that provided the imported file. Not all providers are supported.</small>
                  </b-form-text>
                </b-col>
                
                <b-col>
                  <label class="detail-label" for="timezone">TIMEZONE</label>
                  <b-form-select 
                    id="timezone"
                    v-model="importOptions.timezone" 
                    validated="true"
                    :state="importFormValidation.source"
                    :options="validTimezones"
                    value-field="item"
                    text-field="name"
                    size="sm"
                  />
                  <b-form-text>
                    <small>This is the timezone from which you created the tasks in the import file.</small>
                  </b-form-text>
                </b-col>
              </b-form-row>
              
              <b-form-row class="mt-3">
                <b-col md="6">
                  <label class="detail-label" for="delete">REPLACE DATA</label>
                  <b-form-select 
                    id="delete"
                    v-model="importOptions.delete" 
                    validated="true"
                    :state="importFormValidation.delete"
                    :options="[{name: 'Yes', item: 'Y'}, {name: 'No', item:'N'}]"
                    value-field="item"
                    text-field="name"
                    size="sm"
                  />
                  <b-form-text>
                    <small>Selecting <b>Yes</b> will irrevocably destroy your existing data and replace it with data from the import file.</small>
                  </b-form-text>
                </b-col>
              </b-form-row>

              <b-form-row class="mt-3">
                <b-col>
                  <label class="detail-label" for="dropzone">IMPORT FILE</label>
                  <vue-dropzone 
                    id="dropzone" 
                    ref="myVueDropzone"
                    :options="dropzoneOptions"
                    :duplicate-check="true"
                    :use-custom-slot="true"
                    @vdropzone-complete="importComplete"
                    @vdropzone-file-added="vfileAdded"
                    @vdropzone-success="vsuccess"
                    @vdropzone-error="verror"
                    @vdropzone-removed-file="vremoved"
                    @vdropzone-sending="vsending"
                    @vdropzone-success-multiple="vsuccessMuliple"
                    @vdropzone-sending-multiple="vsendingMuliple"
                    @vdropzone-queue-complete="vqueueComplete"
                    @vdropzone-total-upload-progress="vprogress"
                    @vdropzone-mounted="vmounted"
                    @vdropzone-drop="vddrop"
                    @vdropzone-drag-start="vdstart"
                    @vdropzone-drag-end="vdend"
                    @vdropzone-drag-enter="vdenter"
                    @vdropzone-drag-over="vdover"
                    @vdropzone-drag-leave="vdleave"
                    @vdropzone-duplicate-file="vdduplicate"
                  >
                    <div class="dropzone-custom-content">
                      <p class="h1">Drop file here</p>
                      <div class="subtitle">...or click to select a file from your computer</div>
                    </div>
                  </vue-dropzone>

                  <b-form-valid-feedback :state="importFormValidation.files">
                    Looks good! {{ importFormValidation.files }}
                  </b-form-valid-feedback>

                  <b-form-invalid-feedback :state="importFormValidation.files">
                    Select a file to be imported.
                  </b-form-invalid-feedback>
                </b-col>
              </b-form-row>
              
              <b-row>
                <b-col cols="12" style="text-align:right;">
                  <b-button
                    class="mr-2"
                    variant="warning"
                    type="reset"
                  >
                    Cancel
                  </b-button>
                  <b-button
                    class="mr-2"
                    variant="success"
                    type="submit"
                  >
                    Import
                  </b-button>
                </b-col>
              </b-row>
            </b-form>
          </b-card>

          <!-- Danger Zone (within Data & Privacy tab) -->
          <b-card class="detail-section-card mb-3 border-danger" id="danger-zone">
            <h4 class="detail-section-title text-danger">Danger Zone</h4>
            
            <p class="section-description mb-3">Click the Delete Data button to leave intact your account and, if applicable, your subscription, 
            but delete all of your other data (e.g. context, folders, and tasks). This is useful if you
            want to start anew.</p>
            
            <p class="section-description mb-3">Click the Delete Account button to delete all of your data (e.g. context, folders, and tasks)
            as well as your account and, <u>if <b>not purchased</b> through an app store (the Apple App store or Google Play)</u>, your subscription. This is useful if you
            no longer desire to use Principal Task. Refunds are not offered. <u>Subscriptions purchased through app stores must be managed and cancelled through the respective app store.</u></p>
            
            <p class="section-description text-danger font-weight-bolder mb-3">There is no recovery path for either of these options. The chosen information will 
            be deleted immediately after clicking either button.</p>

            <b-form-group
              id="delete-name-group"
              :state="deleteDataValidation.name" 
              valid-feedback="Looks good!"
              invalid-feedback="This information is required and it must match your name exactly."
            >
              <label class="detail-label" for="name">NAME</label>
              <b-form-input
                id="name"
                v-model.trim="deleteData.name" 
                :state="deleteDataValidation.name"
                size="sm"
              />
              <b-form-text class="section-description mt-2">
                {{ deleteDataDescription }}
              </b-form-text>
            </b-form-group>

            <!-- App Store Subscription Acknowledgment -->
            <b-alert v-if="hasAppStoreSubscription" show variant="warning" class="mb-3">
              <h6 class="mb-2"><strong>App Store Subscription Detected</strong></h6>
              <p class="mb-2">You have an active subscription through an app store (Apple App Store or Google Play Store). 
              Principal Task cannot cancel app store subscriptions automatically.</p>
              <b-form-group :state="deleteDataValidation.appStoreAcknowledgment">
                <b-form-checkbox-compat
                  :model-value="appStoreSubscriptionAcknowledged"
                  @update:modelValue="appStoreSubscriptionAcknowledged = $event"
                  name="appStoreAcknowledgment"
                  :value="true"
                  :unchecked-value="false"
                  :state="deleteDataValidation.appStoreAcknowledgment"
                >
                  <strong>I understand that I must manually cancel my subscription through the app store before or after deleting my account to avoid continued billing.</strong>
                </b-form-checkbox-compat>
                <b-form-invalid-feedback :state="deleteDataValidation.appStoreAcknowledgment">
                  <strong class="text-danger">You must acknowledge this before proceeding.</strong>
                </b-form-invalid-feedback>
              </b-form-group>
            </b-alert>

            <b-row>
              <b-col cols="12" style="text-align:right;">
                <b-button
                  ref="deleteAccountButton"
                  class="mr-2"
                  variant="danger"
                  @click="onSubmitOfDeleteDataForm(true)"
                >
                  Delete Account
                </b-button>
                <b-button
                  ref="deleteDataButton"
                  class="mr-2"
                  variant="warning"
                  @click="onSubmitOfDeleteDataForm(false)"
                >
                  Delete Data
                </b-button>
              </b-col>
            </b-row>
          </b-card>

          </div><!-- /data -->

        </div>
      </template>
    </AppLayout>
  </span>

  <!-- ── MCP Usage Limits Modal ────────────────────────────────────────────── -->
  <div v-if="showMcpLimitsModal" class="modal-backdrop-custom" @click.self="showMcpLimitsModal = false">
    <div class="mcp-limits-dialog card shadow-lg" @click.stop>
      <div class="card-header d-flex justify-content-between align-items-center">
        <strong>MCP Usage Limits</strong>
        <button type="button" class="close" @click="showMcpLimitsModal = false">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <div class="card-body">

        <!-- Premium user: single-column list -->
        <div v-if="isPremiumUser()">
          <table class="mcp-limits-table w-100">
            <tbody>
              <tr><td><i class="fa fa-check-circle text-success mr-2"></i>Rate limit</td><td>{{ mcpLimits.writeRatePaid }} writes · {{ mcpLimits.readRatePaid }} reads per 15 min</td></tr>
              <tr><td><i class="fa fa-check-circle text-success mr-2"></i>Batch operations</td><td>Up to {{ mcpLimits.batchPaid }} tasks ({{ mcpLimits.batchDeletePaid }} for delete)</td></tr>
              <tr><td><i class="fa fa-check-circle text-success mr-2"></i>Connections</td><td>Up to {{ mcpLimits.maxConnectionsPaid }} simultaneous</td></tr>
              <tr><td><i class="fa fa-check-circle text-success mr-2"></i>Activity log</td><td>{{ mcpLimits.retentionPaidDays }}-day retention</td></tr>
              <tr><td><i class="fa fa-check-circle text-success mr-2"></i>Undo</td><td>Available</td></tr>
            </tbody>
          </table>
        </div>

        <!-- Free user: comparison table -->
        <div v-else>
          <table class="mcp-limits-table w-100">
            <thead>
              <tr>
                <th></th>
                <th class="mcp-limits-th">Free <span class="mcp-limits-yours">(Your Plan)</span></th>
                <th class="mcp-limits-th mcp-limits-th--premium">Premium</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="mcp-limits-feature">Rate limit <span class="text-muted small">(per 15 min)</span></td>
                <td>{{ mcpLimits.writeRateFree }} writes · {{ mcpLimits.readRateFree }} reads</td>
                <td class="mcp-limits-td--premium"><i class="fa fa-check-circle text-success mr-1"></i>{{ mcpLimits.writeRatePaid }} writes · {{ mcpLimits.readRatePaid }} reads</td>
              </tr>
              <tr>
                <td class="mcp-limits-feature">Batch operations</td>
                <td>Up to {{ mcpLimits.batchFree }} tasks ({{ mcpLimits.batchDeleteFree }} for delete)</td>
                <td class="mcp-limits-td--premium"><i class="fa fa-check-circle text-success mr-1"></i>Up to {{ mcpLimits.batchPaid }} tasks ({{ mcpLimits.batchDeletePaid }} for delete)</td>
              </tr>
              <tr>
                <td class="mcp-limits-feature">Connections</td>
                <td>Up to {{ mcpLimits.maxConnectionsFree }} simultaneous</td>
                <td class="mcp-limits-td--premium"><i class="fa fa-check-circle text-success mr-1"></i>Up to {{ mcpLimits.maxConnectionsPaid }} simultaneous</td>
              </tr>
              <tr>
                <td class="mcp-limits-feature">Activity log</td>
                <td>{{ mcpLimits.retentionFreeDays }}-day retention</td>
                <td class="mcp-limits-td--premium"><i class="fa fa-check-circle text-success mr-1"></i>{{ mcpLimits.retentionPaidDays }}-day retention</td>
              </tr>
              <tr>
                <td class="mcp-limits-feature">Undo</td>
                <td>Available</td>
                <td class="mcp-limits-td--premium"><i class="fa fa-check-circle text-success mr-1"></i>Available</td>
              </tr>
            </tbody>
          </table>
          <div class="text-right mt-3">
            <button class="btn btn-success" @click="showMcpLimitsModal = false; setTab('account')">
              Upgrade to Premium
            </button>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>

  import vue2Dropzone from 'vue2-dropzone'
  import 'vue2-dropzone/dist/vue2Dropzone.min.css'
  import taskOps from '../mixins/taskOps'
  import genericOps from '../mixins/genericOps'
  import momentTZ from 'moment-timezone'
  import LocalAuth0Service from '../services/LocalAuth0Service';
  import LocalEmailOctopusService from '../services/EmailOctopusService';
  import FolderManagement from '@/components/blocks/FolderManagement.vue'
  import FolderService from '../services/FolderService';
  import Logger from '@/utils/Logger'
  import ContextService from '../services/ContextService';
  import McpSettings from '@/components/McpSettings.vue'
  // 20260506 -- Copilot (Claude Sonnet 4.6) -- Task sharing: manage share links from Profile
  import ShareService from '../services/ShareService'
  import ProfileForm from '@/components/ProfileForm.vue'
  import ReminderConfiguration from '@/components/ReminderConfiguration.vue'
  import SubscriptionManager from '@/components/SubscriptionManager.vue'
  import TaskService from '../services/TaskService';
  import UserService from '../services/UserService';
  import Loading from '@/components/Loading.vue'
  import Menu from '@/components/menus/UserMenu.vue'
  import AppLayout from '@/components/AppLayout.vue'
  import LocalStripeService from '../services/LocalStripeService'
  import md5 from 'md5'

  const log = Logger.create('Profile', { showCaller: true, showLineNumbers: true });

  // 20260419 -- Copilot (Claude Sonnet 4.6) -- Maps legacy hash anchors to tab names for backward-compat deep links
  const HASH_TO_TAB = {
    '#personal-details': 'account',
    '#subscription':     'account',
    '#quick-add':        'organization',
    '#contexts':         'organization',
    '#folders':          'organization',
    '#reminders':        'integrations',
    '#ai-connections':   'integrations',
    '#data-import':      'data',
    '#danger-zone':      'data',
  }
  
	export default {
    name: 'Profile',
    metaInfo() {
        return {
            title: 'Principal Task | Profile',
            meta: []
        }
    },
    components: {
      vueDropzone: vue2Dropzone,
      FolderManagement,
      McpSettings,
      ProfileForm,
      Loading,
      Menu,
      AppLayout,
      ReminderConfiguration,
      SubscriptionManager
			},
    mixins: [taskOps, genericOps],
    data() {
      return {
        // 20260419 -- Copilot (Claude Sonnet 4.6) -- Active tab for profile tab interface
        activeTab: 'account',
        mcpUrlCopied: false,
        showMcpLimitsModal: false,
        accordionModel: {
          contexts: false,
          dataImport: false,
          defaults: false,
          deleteOptions: false,
          folders: false,
          general: false,
          reminder: false,
          subscription: false,
        },
        loading: true,
        deleteDataValidation: {},
        deleteData: {},
        appStoreSubscriptionAcknowledged: false,
        deleteDataDescription: 'Enter your name (' + this.$store.getters.getUser.firstName + ' ' + this.$store.getters.getUser.lastName + ') then click one of the buttons below.  This cannot be undone.  There is no warning.',
        dropzoneOptions: {
          url: '/api/import/toodledo/tasks',
          maxFilesize: 10,
          maxFiles: 1,
          autoProcessQueue: false
        },
        contextAlphaSortDirection: 1,
        contextNumericSortDirection: 1,
        contextValid: [],
        folderValid: [],
        importOptions: {
          source: '',
          delete: 'N',
          files: 0,
          timezone: ''
        },
        localContexts: [],
        localFolders: [],
        validImportSources: [
          {name: 'Toodledo', item: 'toodledo'}
        ],
        importFormValidation: {
          source: null,
          delete: null,
          files: null,
          valid: null
        },
        // 20260506 -- Copilot (Claude Sonnet 4.6) -- Task sharing: Shared Links section state
        allShares:       [],
        sharesLoading:   false,
        sharesLoaded:    false,
        origin:          window.location.origin,
			}
    },
    mounted() {
      this.mountPage()
      
    },
    computed: {
      // 20260217 -- Copilot (Claude Sonnet 4.6) -- Removed isSubscriber computed; template now calls
      // isPremiumUser() from genericOps mixin directly for a single source of truth.
      // 20260419 -- Copilot (Claude Sonnet 4.6) -- MCP server URL from env; falls back to production URL
      mcpServerUrl() {
        return process.env.VUE_APP_ENV_MCP_SERVER_URL || 'https://principaltask.com/api/mcp/v1'
      },
      // 20260419 -- Copilot (Claude Sonnet 4.6) -- MCP limit values from env; same vars used by backend
      mcpLimits() {
        return {
          writeRateFree:       parseInt(process.env.VUE_APP_ENV_MCP_WRITE_RATE_LIMIT_FREE)     || 10,
          writeRatePaid:       parseInt(process.env.VUE_APP_ENV_MCP_WRITE_RATE_LIMIT_PAID)     || 50,
          readRateFree:        parseInt(process.env.VUE_APP_ENV_MCP_READ_RATE_LIMIT_FREE)      || 50,
          readRatePaid:        parseInt(process.env.VUE_APP_ENV_MCP_READ_RATE_LIMIT_PAID)      || 200,
          batchFree:           parseInt(process.env.VUE_APP_ENV_MCP_BATCH_LIMIT_FREE)          || 10,
          batchPaid:           parseInt(process.env.VUE_APP_ENV_MCP_BATCH_LIMIT_PAID)          || 25,
          batchDeleteFree:     parseInt(process.env.VUE_APP_ENV_MCP_BATCH_DELETE_LIMIT_FREE)   || 3,
          batchDeletePaid:     parseInt(process.env.VUE_APP_ENV_MCP_BATCH_DELETE_LIMIT_PAID)   || 10,
          maxConnectionsFree:  parseInt(process.env.VUE_APP_ENV_MCP_MAX_CONNECTIONS_FREE)      || 2,
          maxConnectionsPaid:  parseInt(process.env.VUE_APP_ENV_MCP_MAX_CONNECTIONS_PAID)      || 5,
          retentionFreeDays:   parseInt(process.env.VUE_APP_ENV_MCP_AUDIT_RETENTION_FREE_DAYS) || 30,
          retentionPaidDays:   parseInt(process.env.VUE_APP_ENV_MCP_AUDIT_RETENTION_PAID_DAYS) || 90,
        }
      },
      hasAppStoreSubscription: function() {
        const level = this.$store.getters.getUser.subscriptionLevel
        return level === '$rc_annual' || level === '$rc_monthly'
      },
      gravatarLink: function() {
        return 'https://www.gravatar.com/' + md5(this.$store.getters.getUser.email)
      },
      memberSince: function() {
        if(typeof this.$store.getters.getUser.signupDate != 'undefined' && this.$store.getters.getUser.signupDate !== null) {
         
           return TaskService.getStringFromMillis(this.$store.getters.getUser.signupDate, 'dddd MMMM Do, YYYY')
         }
      },
    },
    watch: {
      $route: 'mountPage',
      fileAdded() {
        let that = this
        setTimeout(function() {
          that.fileAdded = false
        }, 2000)
      },
      filesAdded() {
        let that = this
        setTimeout(function() {
          that.filesAdded = false
        }, 2000)
      },
      success() {
        let that = this
        setTimeout(function() {
          that.success = false
        }, 2000)
      },
      error() {
        let that = this
        setTimeout(function() {
          that.error = false
        }, 2000)
      },
      removedFile() {
        let that = this
        setTimeout(function() {
          that.removedFile = false
        }, 2000)
      },
      sending() {
        let that = this
        setTimeout(function() {
          that.sending = false
        }, 2000)
      },
      successMultiple() {
        let that = this
        setTimeout(function() {
          that.successMultiple = false
        }, 2000)
      },
      sendingMultiple() {
        let that = this
        setTimeout(function() {
          that.sendingMultiple = false
        }, 2000)
      },
      queueComplete() {
        let that = this
        setTimeout(function() {
          that.queueComplete = false
        }, 2000)
      },
      progress() {
        let that = this
        setTimeout(function() {
          that.progress = false
        }, 2000)
      },
      isMounted() {
        let that = this
        setTimeout(function() {
          that.isMounted = false
        }, 2000)
      },
      dDrop() {
        let that = this
        setTimeout(function() {
          that.dDrop = false
        }, 2000)
      },
      dStarted() {
        let that = this
        setTimeout(function() {
          that.dStarted = false
        }, 2000)
      },
      dEnded() {
        let that = this
        setTimeout(function() {
          that.dEnded = false
        }, 2000)
      },
      dEntered() {
        let that = this
        setTimeout(function() {
          that.dEntered = false
        }, 2000)
      },
      dOver() {
        let that = this
        setTimeout(function() {
          that.dOver = false
        }, 2000)
      },
      dLeave() {
        let that = this
        setTimeout(function() {
          that.dLeave = false
        }, 2000)
      },
      dDuplicate() {
        let that = this
        setTimeout(function() {
          that.dDuplicate = false
        }, 2000)
      }
    },
    async created() {
      
      this.loading = true

      // this.$root.$on('bv::collapse::state', (id, state) => {

      //       console.log('bv::collapse::state heard for ' + id + '.  State = ' + state + '.')

      //       if(state) {
      //         VueScrollTo.scrollTo('#' + id)
      //       }

      //   })

      //////console.log('loading = ' + this.loading)
      
      try {
        await this.resetContextData()
        // await this.resetFolderData()
        await this.resetImportData()
        await this.resetUserPreferenceData()
      } catch(e) {
        //console.log('Error loading data for page: ' + e)
      }
      

      this.loading = false

      // 20260419 -- Copilot (Claude Sonnet 4.6) -- Activate tab from hash on initial load (supports mobile deep links like /profile#folders)
      if (this.$route.hash) {
        this.navigateToHash(this.$route.hash)
      }

      //////console.log('loading = ' + this.loading)

    },
    methods: {
      // 20260419 -- Copilot (Claude Sonnet 4.6) -- Copy MCP server URL to clipboard
      async copyMcpUrl() {
        try {
          await navigator.clipboard.writeText(this.mcpServerUrl)
          this.mcpUrlCopied = true
          setTimeout(() => { this.mcpUrlCopied = false }, 2000)
        } catch (e) {
          // silent — clipboard API unavailable in some contexts
        }
      },

      resetDeleteFormState() {
        // Reset form state when collapse is opened
        this.deleteData = {}
        this.deleteDataValidation = {}
        this.appStoreSubscriptionAcknowledged = false
      },
      addContext() {
        // 20260203 -- Copilot (Claude Haiku 4.5) -- Set default private and active to true for new contexts
        this.contextValid.unshift({name: null})
        this.localContexts.unshift({
          addOp: true,
          owner: this.$store.getters.getUser._id,
          active: 'Y',
          private: 'Y'
        })
      },
      
      async onSubmitOfDeleteDataForm(deleteAccount) {

        this.$refs.deleteDataButton.disabled = true
        this.$refs.deleteAccountButton.disabled = true

        this.deleteDataValidation = {}
        this.deleteDataValidation.valid = true

        if(typeof this.deleteData.name == 'undefined' || this.deleteData.name === null) {
          this.deleteDataValidation.valid = false
          this.deleteDataValidation.name = false
        } else {

          if(this.deleteData.name != this.$store.getters.getUser.firstName + ' ' + this.$store.getters.getUser.lastName) {
            this.deleteDataValidation.valid = false
            this.deleteDataValidation.name = false  
          }

        }

        // Check app store subscription acknowledgment if applicable
        if(this.hasAppStoreSubscription && !this.appStoreSubscriptionAcknowledged) {
          this.deleteDataValidation.valid = false
          this.deleteDataValidation.appStoreAcknowledgment = false
        }

        if(!this.deleteDataValidation.valid) {
          this.$refs.deleteDataButton.disabled = false
          this.$refs.deleteAccountButton.disabled = false
          return
        }

        await ContextService.deleteContextsByOwner(this.$store.getters.getUser._id, this.$store.getters.getUserToken).then(function(response) {
                            
            //////console.log('ContextService.deleteContextsByOwner response: ' + JSON.stringify(response))

        }).catch((error) => {
            
            //////console.log('Error deleting contexts: ' + error)
            that.makeToast(true, 'Profile Update', 'Error deleting contexts: ' + error, 'warning')

        })

        await FolderService.deleteFoldersByOwner(this.$store.getters.getUser._id, this.$store.getters.getUserToken).then(function(response) {
                            
            //////console.log('FolderService.deleteFoldersByOwner response: ' + JSON.stringify(response))

        }).catch((error) => {
            
            //////console.log('Error deleting folders: ' + error)
            that.makeToast(true, 'Profile Update', 'Error deleting folders: ' + error, 'warning')

        })


        await TaskService.deleteTasksByOwner(this.$store.getters.getUser._id, this.$store.getters.getUserToken).then(function(response) {
                            
            //////console.log('TaskService.deleteTasksByOwner response: ' + JSON.stringify(response))

        }).catch((error) => {
            
            //////console.log('Error deleting tasks: ' + error)
            that.makeToast(true, 'Profile Update', 'Error deleting tasks: ' + error, 'warning')

        })

        await UserService.deleteUserPrefs(this.$store.getters.getUser._id, this.$store.getters.getUserToken).then(function(response) {
                            
            //////console.log('UserService.deleteUserPrefs response: ' + JSON.stringify(response))

        }).catch((error) => {
            
            //////console.log('Error deleting user preferences: ' + error)
            that.makeToast(true, 'Profile Update', 'Error deleting user preferences: ' + error, 'warning')

        })

        if(deleteAccount) {

          let subscriptionId = this.$store.getters.getUser.stripeSubscriptionId

          // 29-DEC-2025 -- ZWS -- AB#186 -- Unsubscribe from mailing list.
          await LocalEmailOctopusService.unsubscribeUser(this.$store.getters.getUserToken, this.$store.getters.getUser.email).then((response) => {

            log.trace('Unsubscribed from mailing list.');

          }).catch((error) => {
              
              log.error('Error unsubscribing from email list:', error)
              this.makeToast(true, 'Profile Update', 'Error unsubscribing from email list: ' + error, 'warning')

          })

          if(typeof subscriptionId != 'undefined' && subscriptionId !== null) {

            await LocalStripeService.unsubscribeCustomer(this.$store.getters.getUser.stripeSubscriptionId, this.$store.getters.getUserToken).then((response) => {

              log.trace('Subscription cancelled in Stripe.');

            }).catch((error) => {
                
                log.error('Error cancelling subscription in Stripe:', error)
                this.makeToast(true, 'Profile Update', 'Error cancelling subscription: ' + error, 'warning')

            })

          }
          
          await LocalAuth0Service.deleteUser(this.$auth.user.sub, this.$store.getters.getUserToken).then(function(response) {

            log.trace('User deleted from Auth0.');

          }).catch((error) => {
              
              log.error('Error deleting user from Auth0:', error)
              this.makeToast(true, 'Profile Update', 'Error deleting user from Auth0: ' + error, 'warning')

          })
          
          
          await UserService.deleteUser(this.$store.getters.getUser._id, this.$store.getters.getUserToken).then(function(response) {
                            
            log.trace('User deleted from Principal Task database.');

          }).catch((error) => {
              
              log.error('Error deleting user from Principal Task database:', error);
              that.makeToast(true, 'Profile Update', 'Error deleting user: ' + error, 'warning')

          })

          this.$auth.logout({
            returnTo: window.location.origin
          });

        }
        
        

        this.$refs.deleteDataButton.disabled = false
        this.$refs.deleteAccountButton.disabled = false

      },
      onCancelOfContextForm() {
        this.resetContextData()
        this.makeToast(true, 'Profile Update', 'Action cancelled at user\'s request.  No data was changed.', 'warning')
      },
      
      onCancelOfImportForm(evt) {
        evt.preventDefault()

        this.resetImportData()

        this.makeToast(true, 'Data Import', 'Action cancelled at user\'s request.  No data was changed.', 'warning')

      },
      async onSubmitOfContextForm() {
        
        try {
          this.$refs.contextFormSubmitButton.disabled = true
          this.$refs.contextFormResetButton.disabled = true
        } catch(e) {
          // Button refs may not be available in some scenarios
        }
        
        let valid = true

        this.contextValid = []
        
        this.contextValid = this.localContexts.map(x => {
          
          return {name: null}
          
        })

        for(let i = 0; i < this.localContexts.length; i++) {

          if(typeof this.localContexts[i].name == 'undefined' || this.localContexts[i].name === null) {
            this.localContexts[i].name = false
            valid = false
          }

          if(!(typeof this.localContexts[i].customOrder == 'undefined') && !(this.localContexts[i].customOrder === null)) {
            this.localContexts[i].customOrder = Number(this.localContexts[i].customOrder)
          }

        }
        
        if(valid) {
          await this.$store.dispatch('updateContexts', this.localContexts)    
          
          //////console.log('Profile: Contexts updated!')
          
          this.resetContextData()
        } else {
          this.makeToast(true, 'Profile Update', 'Unable to save context changes.  Review context list for details.', 'danger')
        }

      },
      
      onSubmitOfImportForm(evt) {
        evt.preventDefault()

        this.importFormValidation = {}
        this.importFormValidation.valid = true

        if(typeof this.importOptions.source == 'undefined' || this.importOptions.source === null || this.importOptions.source == '') {
          this.importFormValidation.source = false
          this.importFormValidation.valid = false
        }

        if(typeof this.importOptions.delete == 'undefined' || this.importOptions.delete === null || this.importOptions.delete == '') {
          this.importFormValidation.delete = false
          this.importFormValidation.valid = false
        }

        if(this.importOptions.files < 1) {
          this.importFormValidation.files = false
          this.importFormValidation.valid = false
        }

        if(this.importFormValidation.valid) {
          this.$refs.profileImportSubmitButton.disabled = true
          this.$refs.profileImportResetButton.disabled = true
          this.$refs.myVueDropzone.dropzone.options.headers = { Authorization: `Bearer ${this.$store.getters.getUserToken}` };
          this.$refs.myVueDropzone.processQueue()
        } else {
          return
        }


      },
      onSubmitOfProfileForm(updatedUserData) {

        this.updateUser(updatedUserData)
        this.makeToast(true, 'Profile Update', 'Personal details updated successfully.', 'success')

      },
      onSubmitOfReminderConfigurationForm(updatedUserData) {

        this.updateUser(updatedUserData)
        this.makeToast(true, 'Profile Update', 'Reminders configured successfully.', 'success')

      },
      onSubmitOfTaskDefaults(evt) {
        
        evt.preventDefault()

        let temp = this

        this.userPrefs = this.$store.getters.getUserPrefs
        
        this.userPrefs.defaultTaskOptions = this.defaultTaskOptions
        
        this.$store.dispatch('setUserPrefs', this.userPrefs).then(function (response) {

          temp.makeToast(true, 'Profile Update', response.message, 'success')
          
        }).catch((error) => {

          this.makeToast(true, 'Profile Update', error.message, 'warning')

        })
      },
      async prepareContextData() {
        
        let that = this
        
        try {

          await ContextService.getContexts(this.$store.getters.getUser._id, false, this.$store.getters.getUserToken).then(function(response) {

            response.sort(function(a, b) {
              if(a.customOrder > b.customOrder) {
                return 1
              } else if(a.customOrder < b.customOrder) {
                return -1
              } else {
                if(a.name > b.name) {
                  return 1
                } else if(a.name < b.name) {
                  return -1
                } else {
                  return 0
                }
              }
            })
            
            that.localContexts = response
            
            

            that.contextValid = that.localContexts.map(x => {
              return {name: null}
            })
            
          }).catch((error) => {

            //////console.log('Error loading contexts: ' + error)

          })
        } catch(e) {
          //////console.log('Error loading contexts: ' + e)
        }

      },
      
      async prepareUserPreferenceData() {
        
        
        
        let that = this
        
        try {
          
          await UserService.getUserPrefs(this.$store.getters.getUser._id, this.$store.getters.getUserToken).then(function(response) {

            this.defaultTaskOptions = this.$store.getters.getUserPrefs.defaultTaskOptions

            // 2020202 -- ZWS -- BS code to stop Lint from complaining.
            // However, I should really figure out why I'm not doing anything with the response.
            if(this.defaultTaskOptions.length > 10000) {
              //////console.log(response)
            }
          
          }).catch((error) => {

            //////console.log('Error loading folders: ' + error)

          })
        } catch(e) {
          //////console.log('Error loading folder date: ' + e)
        }

      },
      async resetContextData() {
        
        //await this.prepareContextData()

        this.localContexts = await this.$store.dispatch('getContexts') 

        //////console.log('Have ' + this.localContexts.length + ' local contexts')

        /* eslint-disable no-unused-vars */
        this.contextValid = this.localContexts.map(x => {
          /* eslint-enable no-unused-vars */
          return {name: null}
        })

        try {
          this.$refs.contextFormSubmitButton.disabled = false
          this.$refs.contextFormResetButton.disabled = false
        }
        catch(e) {
          // Intentionally left blank
        }
        

      },
      
      resetImportData() {
        this.importFormValidation = {
          source: null,
          delete: null,
          files: null,
          valid: null
        }
        
        this.importOptions = {
          source: '',
          delete: 'N',
          files: 0
        }

        try {
          this.$refs.myVueDropzone.removeAllFiles(true)
          this.$refs.profileImportSubmitButton.disabled = false
          this.$refs.profileImportResetButton.disabled = false
        }
        catch(e) {
          // Intentionally left blank
        }

        this.importOptions.timezone = momentTZ.tz.guess(true)

        let zoneNames = momentTZ.tz.names()

        this.validTimezones = zoneNames.map(x => {
            
          return {name: x, item: x}
        
        })

      },
      async resetUserPreferenceData() {

        let prefs = await this.$store.dispatch('getUserPrefs')
        
        //////console.log('Have prefs = ' + JSON.stringify(prefs))

        this.defaultTaskOptions = prefs.defaultTaskOptions

        //////console.log('Have defaultTaskOptions = ' + JSON.stringify(this.defaultTaskOptions))

      },
      // 20260416 -- Copilot (Claude Sonnet 4.6) -- Toggle wrappers for context sort; drive the existing direction-param methods from UI state
      toggleSortContextsAlpha() {
        this.contextAlphaSortDirection = this.contextAlphaSortDirection * -1
        this.sortContextsAlpha(this.contextAlphaSortDirection)
      },
      toggleSortContextsCustom() {
        this.contextNumericSortDirection = this.contextNumericSortDirection * -1
        this.sortContextsCustom(this.contextNumericSortDirection)
      },
      sortContextsAlpha(direction) {
        
        //////console.log('Profile.sortContextsAlpha(' + direction + ')')
        
        this.localContexts.sort(function(a, b) {
          if(a.name > b.name) {
            return 1*direction
          } else if(a.name < b.name) {
            return -1*direction
          } else {
            if(a.customOrder > b.customOrder) {
              return 1*direction
            } else if(a.customOrder < b.customOrder) {
              return -1*direction
            } else {
              return 0
            }
          }
        })

        for(let i = 0; i < this.localContexts.length; i++) {
          this.localContexts[i].customOrder = (i+1)*5
        }

      },
      sortContextsCustom(direction) {

        
        //////console.log('Profile.sortContextsCustom(' + direction + ')')

        this.localContexts.sort(function(a, b) {
          if(a.customOrder > b.customOrder) {
            return 1*direction
          } else if(a.customOrder < b.customOrder) {
            return -1*direction
          } else {
            if(a.name > b.name) {
              return 1*direction
            } else if(a.name < b.name) {
              return -1*direction
            } else {
              return 0
            }
          }
        })

        for(let i = 0; i < this.localContexts.length; i++) {
          this.localContexts[i].customOrder = (i+1)*5
        }

      },
      
      toggleContextField(index, field) {
        
        
        if(typeof this.localContexts[index]._id == 'undefined' || this.localContexts[index]._id === null)
        {

          this.localContexts.splice(index, 1)
          this.localContexts.splice(index, 1)

        } else {

          let val = this.localContexts[index][field]

          if(typeof val == 'undefined' || val === null) {
            val = true
          } else if(val) {
            val = false
          } else {
            val = true
          }
          
          this.$set(this.localContexts[index], field, val)
        }
      
      },
      
      mountPage() {
        let message = this.$route.params.message

        if(typeof message != 'undefined' && message !== null) {
          this.makeToast(true, message.title, message.body, message.variant)
        }

        // 20260419 -- Copilot (Claude Sonnet 4.6) -- Activate tab when route hash changes (e.g. sidebar manage links: /profile#folders)
        if (this.$route.hash) {
          this.navigateToHash(this.$route.hash)
        }
      },
      // 20260419 -- Copilot (Claude Sonnet 4.6) -- Switch active profile tab
      setTab(tab) {
        this.activeTab = tab
      },
      // 20260419 -- Copilot (Claude Sonnet 4.6) -- Switch tab from a hash anchor then scroll to the element
      // after the next DOM update (v-show must apply before scrollIntoView has a position to target)
      navigateToHash(hash) {
        const tab = HASH_TO_TAB[hash]
        if (!tab) return
        this.activeTab = tab
        this.$nextTick(() => {
          const el = document.getElementById(hash.slice(1))
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        })
      },
      importComplete(response) {
        ////////console.log('importComplete Response (1) = ' + response)
        ////////console.log('importComplete Response (2) = ' + JSON.stringify(response))
      },
      
      vfileAdded(file) {
        this.importOptions.files += 1;
        this.fileAdded = true
        //////console.log(file)
      },
      vfilesAdded(file) {
        this.importOptions.files += 1;
        this.filesAdded = true
        //////console.log(file)
        // window.toastr.info('', 'Event : vdropzone-files-added')
      },
      vsuccess(file, response) {

        let result = response.result

        if(result.successful) {
          this.$store.dispatch('loadTasks', {})
          this.makeToast(true, 'Data Import', result.message, 'success')
        } else {
          this.$store.dispatch('loadTasks', {})
          this.makeToast(true, 'Data Import', result.message, 'warning')
        }

        this.resetImportData()

        this.success = true

      },
      verror(file) {
        this.$store.dispatch('loadTasks', {})
        this.makeToast(true, 'Data Import', 'An unknown error was encountered processing: ' + file.filename, 'warning')
        this.resetImportData()
        this.error = true
        //////console.log(file)
        // window.toastr.error(file.upload.filename, 'Event : vdropzone-error - ' + file.status)
      },
      vremoved(file, xhr, error) {
        this.removedFile = true
        //////console.log(xhr + error)
        // window.toastr.warning('', 'Event : vdropzone-removedFile')
      },
      vsending(file, xhr, formData) {
        
        this.sending = true
        
        formData.append('taskOwner', this.$store.getters.getUser._id)
        //formData.append('importOptions', JSON.stringify(this.importOptions)

        let keys = Object.keys(this.importOptions)
        let values = Object.values(this.importOptions)
        let i = 0
        for (i = 0; i < keys.length; i++) {
          formData.append(keys[i], values[i])
        }

        // window.toastr.warning('', 'Event : vdropzone-sending')
      },
      vsuccessMuliple(files, response) {
        this.successMultiple = true
        //////console.log(files)
        // window.toastr.success('', 'Event : vdropzone-success-multiple')
      },
      vsendingMuliple(file, xhr, formData) {
        this.sendingMultiple = true
        //////console.log(file, xhr, formData)
        // window.toastr.warning('', 'Event : vdropzone-sending-multiple')
      },
      vqueueComplete(file, xhr, formData) {
        this.queueComplete = true
        //////console.log(file, xhr, formData)
        // window.toastr.success('', 'Event : vdropzone-queue-complete')
      },
      vprogress(totalProgress, totalBytes, totalBytesSent) {
        this.progress = true
        this.myProgress = Math.floor(totalProgress)
        // window.toastr.success('', 'Event : vdropzone-sending')
      },
      vmounted() {
        //////console.log("DropZone mounted");
        this.isMounted = true
      },
      vddrop() {
        this.dDrop = true
      },
      vdstart() {
        this.dStarted = true
      },
      vdend() {
        this.dEnded = true
      },
      vdenter() {
        this.dEntered = true
      },
      vdover() {
        this.dOver = true
      },
      vdleave() {
        this.dLeave = true
      },
      vdduplicate() {
        this.dDuplicate = true
      },
      updateUser(userData) {

        this.$store.dispatch('updateUser', userData)
      },

      // 20260506 -- Copilot (Claude Sonnet 4.6) -- Task sharing: Shared Links management
      async loadSharedLinks() {
        if (this.sharesLoading) return;
        this.sharesLoading = true;
        try {
          const res = await ShareService.getShares(this.$store.getters.getUserToken);
          this.allShares   = res.data?.data || [];
          this.sharesLoaded = true;
        } catch (_) {
          this.allShares = [];
          // 20260508 -- Copilot (Claude Sonnet 4.6) -- Set sharesLoaded so UI exits pre-load state on error
          this.sharesLoaded = true;
          this.makeToast(false, 'Shared Links', 'Could not load shared links.', 'danger');
        } finally {
          this.sharesLoading = false;
        }
      },

      async revokeSharedLink(shareToken) {
        try {
          await ShareService.revokeShare(shareToken, this.$store.getters.getUserToken);
          this.makeToast(true, 'Shared Links', 'Link revoked.', 'success');
          await this.loadSharedLinks();
        } catch (_) {
          this.makeToast(false, 'Shared Links', 'Could not revoke link.', 'danger');
        }
      },

      formatSharedLinkDate(epochMs) {
        if (!epochMs) return '—';
        return new Date(epochMs).toLocaleDateString('en-US', {
          month: 'short', day: 'numeric', year: 'numeric',
        });
      },
    }
  }
</script>

<style scoped>
.profile-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
}

.profile-header {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding: 1.25rem;
  background: white;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.profile-avatar {
  flex-shrink: 0;
}

.profile-info {
  flex: 0 1 auto;
}

.profile-name {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0.15rem;
  color: var(--text-primary);
}

.profile-meta {
  color: var(--text-secondary);
  margin-bottom: 0.4rem;
  font-size: 0.9rem;
}

.profile-link {
  font-size: 0.8rem;
}

/* 20260419 -- Copilot (Claude Sonnet 4.6) -- Tab navigation replacing anchor-link quick nav */
.profile-tabs {
  display: flex;
  flex-wrap: wrap;
  border-bottom: 2px solid #e9ecef;
  gap: 0.25rem;
}

.profile-tab {
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  padding: 0.5rem 1.25rem;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  color: #6c757d;
  transition: color 0.15s, border-color 0.15s;
}

.profile-tab:hover {
  color: #343a40;
}

.profile-tab.active {
  color: #343a40;
  border-bottom-color: #78CB5B;
  font-weight: 600;
}

.subscriber-status {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-left: auto;
  padding: 0.5rem 0;
  position: relative;
}

.status-accent {
  width: 3px;
  height: 40px;
  background: linear-gradient(180deg, #FFC107 0%, #78CB5B 100%);
  border-radius: 2px;
}

.status-icon {
  font-size: 1.5rem;
  color: #FFC107;
}

.status-text {
  text-align: right;
  padding-right: 0.5rem;
}

.status-level {
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.95rem;
  letter-spacing: 0.05em;
}

.status-tier {
  font-size: 0.75rem;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-top: 0.1rem;
}

.subscriber-badge {
  display: none;
}

.detail-section-title {
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: var(--spacing-md);
  padding-bottom: var(--spacing-sm);
  border-bottom: 2px solid var(--accent-color);
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.detail-label {
  font-size: 0.625rem;
  font-weight: 500;
  color: var(--text-secondary);
  margin-bottom: var(--spacing-xs);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  display: block;
}

.section-description {
  font-size: 0.875rem;
  color: var(--text-secondary);
  line-height: 1.5;
}

.mcp-server-url {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  background: #f0f8ff;
  border: 1px solid #b6d0ff;
  border-left: 4px solid #78CB5B;
  border-radius: 6px;
  padding: 0.6rem 1rem;
}

.mcp-url-code {
  font-size: 0.9rem;
  color: #1a4fbf;
  background: none;
  padding: 0;
  word-break: break-all;
}

.mcp-url-copy {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: 1px solid #b6d0ff;
  border-radius: 0.3rem;
  background: rgba(255,255,255,0.7);
  color: #1a4fbf;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
  line-height: 1;
}

.mcp-url-copy:hover {
  background: #fff;
  border-color: #78CB5B;
  color: #0e7052;
}

.mcp-url-copy--copied {
  background: #d1f5ec;
  border-color: #a8e8d6;
  color: #0e7052;
}

.mcp-footnote {
  font-size: 0.75rem;
  color: var(--text-secondary);
  padding-left: 0.25rem;
}

.mcp-limits-trigger {
  font-size: 0.75rem;
}

.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.mcp-limits-dialog {
  width: 100%;
  max-width: 640px;
  z-index: 1060;
}

.mcp-limits-table {
  font-size: 0.875rem;
  border-collapse: collapse;
}

.mcp-limits-table thead tr {
  border-bottom: 2px solid #dee2e6;
}

.mcp-limits-table tbody tr {
  border-bottom: 1px solid #f0f0f0;
}

.mcp-limits-table tbody tr:last-child {
  border-bottom: none;
}

.mcp-limits-table td,
.mcp-limits-table th {
  padding: 0.6rem 0.75rem;
  vertical-align: middle;
}

.mcp-limits-th {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6c757d;
  text-align: left;
}

.mcp-limits-th--premium {
  color: #0e7052;
  background: #f5fbf2;
  border-radius: 4px 4px 0 0;
}

.mcp-limits-td--premium {
  background: #f5fbf2;
  color: #1a3d2b;
}

.mcp-limits-feature {
  color: #6c757d;
  white-space: nowrap;
}

.mcp-limits-yours {
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  font-size: 0.7rem;
}

.task-card-actions-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
  margin-top: 1rem;
}

.task-card-actions-footer .btn {
  flex-shrink: 0;
  width: auto !important;
  display: inline-block !important;
  min-width: fit-content;
}

/* 20260416 -- Copilot (Claude Sonnet 4.6) -- Action bar for context/folder toolbar sections */
.section-action-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.375rem 0;
}

.action-bar-sort {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex-wrap: wrap;
}

.action-bar-label {
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding-right: 0.125rem;
}

.action-bar-sort-btn {
  display: inline-flex !important;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.8125rem;
}

.action-bar-add-btn {
  display: inline-flex !important;
  align-items: center;
  gap: 0.25rem;
  font-weight: 600;
}

.active {
  color: #78CB5B;
}
.inactive {
  color: #fff000;
}
.fa.fa-circle:before {
  content: "\25C9";
  /*color: #000;*/
}
th {
  text-align: center;
}
td:nth-child(1) {
  text-align: center;
}
td:nth-child(3) {
  text-align: center;
}
td:nth-child(2) {
  padding-left: 40px;
}
.event-active {
  font-weight: bold;
  color: #78CB5B;
  text-transform: uppercase;
  letter-spacing: 1.2px;
}
</style>